import {
  Button,
  Card,
  CardBody,
  Divider,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { ClientRegistrationMetadata, TokenResultBody } from '@llx/common';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';

import { AccessLinkTypes, useIsValidAccessLinkQuery } from '@/@generated/codegen/API';
import { persistTokensRaw } from '@/auth/keycloak';
import { axiosClient } from '@/axiosClient';
import { config } from '@/environment';
import { ROUTES } from '@/routes/Routes';
import PageLayout from '@/ui/Layout/PageLayout';
import { LivelinxLogo } from '@/ui/Logo';
import { PageLoading } from '@/ui/PageLoading';

type FormData = {
  password: string;
  confirmPassword: string;
};

export function RegisterPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const { accessCode } = useParams();
  const [isRegistering, setIsRegistering] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<FormData>();

  const password = watch('password');

  if (!accessCode) {
    return <Navigate to="/" />;
  }

  const { data: validAccessLinkQuery, loading: isValidAccessLinkLoading } =
    useIsValidAccessLinkQuery({
      variables: { code: accessCode },
    });

  const validAccessLink = useMemo(
    () => validAccessLinkQuery?.isValidAccessLink ?? null,
    [validAccessLinkQuery]
  );

  const metadata = useMemo(() => {
    if (!validAccessLink) return null;
    return validAccessLink.metadata as ClientRegistrationMetadata | null;
  }, [validAccessLink]);

  const isRegistrationLink = validAccessLink?.purpose === AccessLinkTypes.ClientRegistration;

  const onSubmit = async (formData: FormData) => {
    setIsRegistering(true);
    try {
      const response = await axiosClient.post<TokenResultBody>(
        `${config.BACKEND_URL}/auth/register`,
        {
          code: accessCode,
          password: formData.password,
        },
        { timeout: 15000 }
      );

      const { access_token, refresh_token, id_token } = response.data;
      if (access_token && refresh_token && id_token) {
        persistTokensRaw(access_token, refresh_token, id_token);
      }

      toast({
        status: 'success',
        title: 'Account created',
        description: 'Your account has been created successfully. Redirecting...',
        duration: 3000,
      });

      setTimeout(() => {
        window.location.href = ROUTES.HOME;
      }, 1500);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Registration failed. Please try again.';
      toast({
        status: 'error',
        title: 'Registration failed',
        description: message,
        duration: 5000,
        isClosable: true,
      });
      setIsRegistering(false);
    }
  };

  if (isValidAccessLinkLoading) {
    return <PageLoading />;
  }

  if (!validAccessLink || !isRegistrationLink || !metadata) {
    toast({
      status: 'error',
      title: 'Invalid link',
      description: 'This registration link is invalid or has expired.',
    });
    setTimeout(() => {
      navigate(ROUTES.HOME);
    }, 3000);
    return <PageLoading />;
  }

  return (
    <PageLayout showHeader={false}>
      <HStack padding={4}>
        <Link to={ROUTES.HOME} reloadDocument>
          <LivelinxLogo />
        </Link>
      </HStack>
      <Flex width="100%" height="100%" alignItems="center" justifyContent="center" flexDir="column">
        <Card
          maxW={{ base: '90%', md: '450px' }}
          width="100%"
          px={4}
          shadow="2xl"
          border="1px solid black"
        >
          <CardBody>
            <Text fontSize="2xl" width="100%" textAlign="center">
              Create your account
            </Text>
            <Divider my={4} width="100%" />
            <form onSubmit={(e) => void handleSubmit(onSubmit)(e)}>
              <fieldset disabled={isRegistering}>
                <VStack gap="16px" pb="10px">
                  <FormControl>
                    <FormLabel fontSize="sm" fontWeight="normal">
                      Email
                    </FormLabel>
                    <Input type="email" value={metadata.email} isReadOnly bg="gray.50" />
                  </FormControl>

                  <FormControl isRequired isInvalid={Boolean(errors.password)}>
                    <FormLabel fontSize="sm" fontWeight="normal">
                      Password
                    </FormLabel>
                    <Input
                      type="password"
                      placeholder="Choose a password"
                      {...register('password', {
                        required: 'Password is required',
                        minLength: {
                          value: 8,
                          message: 'Password must be at least 8 characters',
                        },
                      })}
                    />
                    {errors.password && (
                      <FormHelperText color="red">{errors.password.message}</FormHelperText>
                    )}
                  </FormControl>

                  <FormControl isRequired isInvalid={Boolean(errors.confirmPassword)}>
                    <FormLabel fontSize="sm" fontWeight="normal">
                      Confirm password
                    </FormLabel>
                    <Input
                      type="password"
                      placeholder="Confirm your password"
                      {...register('confirmPassword', {
                        required: 'Please confirm your password',
                        validate: (value) => value === password || 'Passwords do not match',
                      })}
                    />
                    {errors.confirmPassword && (
                      <FormHelperText color="red">{errors.confirmPassword.message}</FormHelperText>
                    )}
                  </FormControl>

                  <Button
                    type="submit"
                    width="full"
                    background="blue.400"
                    color="white"
                    variant="solid"
                    isLoading={isRegistering}
                    isDisabled={isRegistering}
                  >
                    Create account
                  </Button>
                </VStack>
              </fieldset>
            </form>
          </CardBody>
        </Card>
      </Flex>
    </PageLayout>
  );
}
